// Event dates where the date and the time are independent: an event can have
// both, only a date, only a time, or neither.
//
// Stored shapes of the single event_date string:
//   "2026-10-28T17:45:00"  date and time
//   "2026-10-28"           date only
//   "T17:45:00"            time only
//   ""                     neither
//
// Older builds always stored `${date}T${time}:00`, so clearing the time box wrote
// values like "2026-10-28T:00" or "T:00:00". Nothing here ever renders those as
// "Invalid Date": a missing or unusable part formats as an empty string, and the
// callers hide that row entirely.

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_TIME = /^\d{2}:\d{2}/;

// Splits a stored value into the pieces the date/time inputs need. Each part is
// read on its own, so a value can yield a date, a time, both or neither.
export const splitEventDateTime = (value) => {
  if (typeof value !== "string" || !value.trim()) return { date: "", time: "" };
  const [datePart = "", timePart = ""] = value.trim().split("T");
  return {
    date: DATE_ONLY.test(datePart) ? datePart : "",
    time: HAS_TIME.test(timePart) ? timePart.slice(0, 5) : "",
  };
};

// Builds the value to store from whichever parts were filled in.
export const buildEventDateTime = (date, time) => {
  if (date && time) return `${date}T${time}:00`;
  if (date) return date;
  if (time) return `T${time}:00`;
  return "";
};

// The edit form writes only event_date, so once that key exists — even as ""
// after both boxes were cleared — it is authoritative over the start/end pair
// written when the event was created.
const hasOwnEventDate = (event) =>
  event.event_date !== undefined && event.event_date !== null;

// The raw date and time an event carries, for filling the edit form. Ignores
// hide_date on purpose: hiding is a display choice, and the form must not
// wipe the stored values just because they are hidden.
export const readEventDateTime = (event) => {
  if (!event) return { date: "", time: "" };
  return splitEventDateTime(
    hasOwnEventDate(event) ? event.event_date : event.event_start_date
  );
};

// What should be displayed. hide_date suppresses everything; a separate end
// date only counts for events still on their creation-time start/end pair.
export const getEventDateParts = (event) => {
  if (!event || event.hide_date) return { date: "", time: "", endDate: "" };
  const { date, time } = readEventDateTime(event);
  if (hasOwnEventDate(event)) return { date, time, endDate: "" };
  const end = splitEventDateTime(event.event_end_date).date;
  return { date, time, endDate: end && date && end !== date ? end : "" };
};

// "2026-10-28" read in local time. `new Date("2026-10-28")` would be parsed as
// UTC midnight and render as the previous day west of Greenwich.
export const toLocalDate = (ymd) => {
  const [year, month, day] = ymd.split("-").map(Number);
  return new Date(year, month - 1, day);
};

// "" when the event has no date to show.
export const formatEventDate = (event) => {
  const { date, endDate } = getEventDateParts(event);
  if (!date) return "";
  const start = toLocalDate(date).toLocaleDateString();
  return endDate ? `${start} - ${toLocalDate(endDate).toLocaleDateString()}` : start;
};

// Renders "17:45" in the viewer's locale, e.g. "05:45 PM".
export const formatClockTime = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  // Any calendar day will do — only the time is rendered.
  return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// "" when the event has no time to show — with or without a date.
export const formatEventTime = (event) => {
  const { time } = getEventDateParts(event);
  return time ? formatClockTime(time) : "";
};
